import { getWorkspaceId } from '../../auth/workspaceService';

/**
 * What quick add remembers on this device, per workspace: the accounts it
 * used last, what each name was filed under last time, and a draft that could
 * not be saved. All of it is safe to clear.
 */

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_MEMORY = 200;
const MAX_RECENT_ACCOUNTS = 5;

const key = (name) => `mk.quickAdd.${name}.${getWorkspaceId() ?? ''}`;

function read(name) {
  try {
    const raw = localStorage.getItem(key(name));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(name, value) {
  try {
    if (value == null) localStorage.removeItem(key(name));
    else localStorage.setItem(key(name), JSON.stringify(value));
  } catch {
    // A full or blocked storage only costs the convenience.
  }
}

/** Account ids, most recently used first. */
export function recentAccountIds() {
  const ids = read('accounts');
  return Array.isArray(ids) ? ids : [];
}

export function rememberAccount(id) {
  if (id == null) return;
  write('accounts', [id, ...recentAccountIds().filter((x) => x !== id)].slice(0, MAX_RECENT_ACCOUNTS));
}

const memoryKey = (type, note) => `${type}|${String(note).trim().toLowerCase()}`;

/** What `note` of `type` was filed under last time: `{ category_id, account_id, from_account_id, to_account_id, currency }`. */
export function recallMovement(type, note) {
  const memory = read('memory');
  return (memory && memory[memoryKey(type, note)]) || null;
}

export function rememberMovement(type, note, values) {
  if (!String(note || '').trim()) return;
  const memory = read('memory') || {};
  const k = memoryKey(type, note);
  delete memory[k];
  memory[k] = { ...values, at: Date.now() };
  // Oldest first in insertion order: keep the newest.
  const keys = Object.keys(memory);
  keys.slice(0, Math.max(0, keys.length - MAX_MEMORY)).forEach((old) => delete memory[old]);
  write('memory', memory);
}

/**
 * The draft kept after a save that failed (or a sheet that went away with
 * content), for 24 hours. `guard` says the next save must first look for the
 * movement having been created after all.
 */
export function loadDraft() {
  const saved = read('draft');
  if (!saved?.draft || Date.now() - (saved.savedAt || 0) > DRAFT_TTL_MS) {
    if (saved) write('draft', null);
    return null;
  }
  return saved;
}

export function saveDraft(draft, { guard = false } = {}) {
  write('draft', { draft, guard, savedAt: Date.now() });
}

export function clearDraft() {
  write('draft', null);
}
